import TokenAvatar from '@components/TokenAvatar'
import { config } from '@plug-core/appConfig'
import ICPSwap from '@plug-core/clients/ICPSwap'
import useConfig from '@plug-core/hooks/useConfig'
import { Chain } from '@plug-core/interfaces/common'
import { SwapProvider, SwapTokenPair } from '@plug-core/interfaces/swapInterface'
import { logger } from '@plug-core/logger'
import { useCurrentWallet } from '@plug-core/state'
import { formatNumber } from '@plug-core/utils/formatNumber'
import { subaccountFromPrincipal } from '@plug-core/utils/subaccount'
import getTokenMetadata from '@plug-core/utils/tokenActions/getMetadata'
import getTokenBalance from '@plug-core/utils/tokenActions/tokenBalance'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { Button, IconButton, ListGroup, ListRow, Spinner } from '@ui'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineArrowUturnDown } from 'react-icons/hi2'
import { useLocation } from 'react-router-dom'

interface PairFallbackState {
  pair: SwapTokenPair
}

interface Balance {
  canisterPrincipal: string
  tokenStandard: string
  balance: number
  type: 'undeposited' | 'unwithdrawn'
}

const ICPSwapFallback = () => {
  const [balances, setBalances] = useState<Balance[]>([])
  const { pair } = useLocation().state as PairFallbackState

  const { currentWallet } = useCurrentWallet()
  const { tokens } = useConfig()

  const [balanceInWithdraw, setBalanceInWithdraw] = useState<Balance>()
  const [refreshBalances, setRefreshBalances] = useState<number>()

  const { t } = useTranslation()

  const fallback = useCallback(
    async (balance: Balance) => {
      if (pair.provider === SwapProvider.ICPSWAP) {
        const icpSwapService = new ICPSwap(config.getAgent({ principalId: currentWallet }))

        try {
          const tokenMetadata = await getTokenMetadata(balance.canisterPrincipal, Chain.ICP, {
            standard: balance.tokenStandard,
            agent: config.getAgent(),
          })
          if (!tokenMetadata) return

          const poolActor = icpSwapService.getPoolActor(pair.swapCanisterPrincipal, true)

          if (balance.type === 'undeposited') {
            await poolActor.deposit({
              fee: tokenMetadata.fee,
              token: balance.canisterPrincipal,
              amount: BigInt(balance.balance) - tokenMetadata.fee,
            })
          }

          await poolActor.withdraw({
            fee: tokenMetadata.fee,
            token: balance.canisterPrincipal,
            amount:
              BigInt(balance.balance) -
              (balance.type === 'undeposited' ? 2n : 1n) * tokenMetadata.fee,
          })
        } catch (e) {
          logger.error(
            `Could not fetch ICPSwap fallback token(${balance.canisterPrincipal}) metadata`,
            { originalError: e }
          )
        }
      }
    },
    [pair]
  )

  const performFallback = useCallback(
    (balance: Balance) => {
      setBalanceInWithdraw(balance)

      fallback(balance)
        .then(() => {
          setRefreshBalances(Date.now())
        })
        .catch((e) =>
          logger.error('Failed to perform ICPSwap fallback', {
            originalError: e,
          })
        )
        .finally(() => setBalanceInWithdraw(undefined))
    },
    [fallback]
  )

  useEffect(() => {
    const agent = config.getAgent()

    setBalances([])

    if (pair.provider === SwapProvider.ICPSWAP) {
      const tokens = [pair.token0, pair.token1]
      tokens.map((token) => {
        getTokenBalance(
          token.principal,
          token.standard,
          agent,
          pair.swapCanisterPrincipal,
          subaccountFromPrincipal(currentWallet)
        ).then((balance) => {
          setBalances((currentValue) => [
            ...currentValue,
            {
              canisterPrincipal: token.principal,
              tokenStandard: token.standard,
              balance: Number(balance),
              type: 'undeposited',
            },
          ])
        })
      })

      const icpSwapService = new ICPSwap(agent)

      icpSwapService.getPoolUnusedBalance(pair, currentWallet).then((unusedBalances) => {
        setBalances((currentValue) => [
          ...currentValue,
          {
            canisterPrincipal: pair.token0.principal,
            tokenStandard: pair.token0.standard,
            balance: unusedBalances.balance0,
            type: 'unwithdrawn',
          },
          {
            canisterPrincipal: pair.token1.principal,
            tokenStandard: pair.token1.standard,
            balance: unusedBalances.balance1,
            type: 'unwithdrawn',
          },
        ])
      })
    }
  }, [pair, currentWallet, refreshBalances])

  const renderBalanceRow = (balance: Balance, index: number) => {
    const tokenDetails = tokens.find((token) => token.address === balance.canisterPrincipal)

    return (
      <ListRow
        key={index}
        leading={<TokenAvatar logo={tokenDetails?.logo} fallbackName={tokenDetails?.symbol} />}
        primary={
          <span className="tabular">
            {formatNumber(e8sToDecimals(balance.balance, tokenDetails?.decimals || 8))}
          </span>
        }
        secondary={tokenDetails?.symbol}
        trailing={
          <>
            {balanceInWithdraw === balance && <Spinner size={20} />}
            {!balanceInWithdraw && (
              <IconButton
                aria-label={t('common.withdraw')}
                onClick={() => performFallback(balance)}
              >
                <HiOutlineArrowUturnDown />
              </IconButton>
            )}
          </>
        }
      />
    )
  }

  return (
    <div className="mb-3 flex flex-col gap-4">
      {balances.length > 0 && <ListGroup>{balances.map(renderBalanceRow)}</ListGroup>}
      {pair.provider === SwapProvider.ICPSWAP && (
        <Button
          variant="link"
          size="sm"
          onClick={() => window.open('https://app.icpswap.com/swap/withdraw?type=pair', '_blank')}
          className="self-center"
        >
          {t('swap.fallbackICPSwap')}
        </Button>
      )}
    </div>
  )
}

export default ICPSwapFallback
